import { fetcher, type IFetcher } from '@/src/general/utilities/fetcher'
import type {
  TCommonMetaProps,
  TFieldNames,
  TFields,
  TMeta,
  TMetaNames,
} from '@/src/general/utilities/fetcher/api.types'
import type { TSignalConfig } from '@/src/general/utilities/fetcher/IFetcher'
import type { PUBLICATION_TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'

import { PUBLICATION_ORDER } from '../models/publication.constants'
import type { TGetPublicationsPayload, TPublicationApi } from './types/getPublication.types'
import type { TGetPublicationsData } from './types/getPublications.types'
import type { TFavoritePublicationApi, TPublicationApiOptionalAttributes } from './types/publicationApi.types'

export type TMarkPublicationAsReadProps = {
  publicationId: number
  userId: number
  source: PUBLICATION_TRACKING_SOURCE
}

class PublicationRepositoryImpl {
  constructor(private apiFetcher: IFetcher) {}

  getPublication = <Fields extends TFields<Pick<TPublicationApiOptionalAttributes, 'sharesCount'>>>(
    id: number,
    { fields, signal }: TSignalConfig & Fields,
  ) => this.apiFetcher.query<TPublicationApi<TFieldNames<Fields>>>(`/publication/${id}`, { params: { fields }, signal })

  getPublications = <Fields extends TFields<TPublicationApiOptionalAttributes>, Meta extends TMeta<TCommonMetaProps>>(
    {
      limit = 10,
      offset = 0,
      order = PUBLICATION_ORDER.ORDER_FRESHNESS,
      filter,
      ...otherParams
    }: TGetPublicationsPayload & Fields & Meta,
    { signal }: TSignalConfig & Fields & Meta,
  ) =>
    this.apiFetcher.query<TGetPublicationsData<TFieldNames<Fields>, TMetaNames<Meta>>>(`/publications`, {
      params: { limit, offset, order, filter, ...otherParams },
      signal,
    })

  markPublicationAsRead = ({ publicationId, userId, source }: TMarkPublicationAsReadProps) =>
    this.apiFetcher.mutate<void>(`/publication/${publicationId}/mark-as-read`, { userId, source }, { method: 'POST' })

  markAsFavorite = (publicationId: number) =>
    this.apiFetcher.mutate<{ favorite: TFavoritePublicationApi }, TNoMoreProps>(
      `/publication/${publicationId}/favorite`,
      {},
      { method: 'POST' },
    )

  unmarkAsFavorite = (publicationId: number) =>
    this.apiFetcher.mutate<void>(`/publication/${publicationId}/favorite`, {}, { method: 'DELETE' })

  getFavoritePublications = <
    Fields extends TFields<TPublicationApiOptionalAttributes>,
    Meta extends TMeta<TCommonMetaProps>,
  >(
    {
      limit = 10,
      offset = 0,
      order = PUBLICATION_ORDER.ORDER_FRESHNESS,
      filter,
      ...otherParams
    }: TGetPublicationsPayload & Fields & Meta,
    { signal }: TSignalConfig & Fields & Meta,
  ) =>
    this.apiFetcher.query<TFavoritePublicationApi[]>(`/me/favorites`, {
      params: { limit, offset, order, filter, ...otherParams },
      signal,
    })
}

export const publicationApi = new PublicationRepositoryImpl(fetcher)
